import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { applyAuction } from "@/lib/api";

const CONSENTS = [
  { key: "consent_review", label: "I understand an application is not automatic acceptance and every dog must pass a behavioral, health and safety review." },
  { key: "consent_decline", label: "I understand Toriano may decline an unsafe or unsuitable placement." },
  { key: "consent_travel", label: "I understand travel, lodging, dog transportation and event tickets are NOT included unless stated in writing." },
  { key: "consent_updates", label: "I agree to receive application updates by email, text and/or WhatsApp." },
];

const HEARD_OPTIONS = ["TikTok Live", "TikTok video", "Instagram", "Facebook", "YouTube", "Podcast", "Referral", "Google", "Event", "Other"];
const MERCH_SIZES = ["XS","S","M","L","XL","XXL","3XL","Prefer to discuss"];
const SEX_OPTIONS = ["Male", "Female", "Male (neutered)", "Female (spayed)"];

export default function ApplicationForm({ open, onClose, pkg, source = "website" }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null); // { application_number, next_step, reservation_recommended }
  const [reservationDone, setReservationDone] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", phone: "", whatsapp: "", city: "", country: "",
    dog_name: "", breed: "", dog_age: "", dog_sex: "", dog_weight: "",
    challenges: "", bite_history: "", medical: "",
    destination: "", bid_amount: "", preferred_dates: "", merch_size: "",
    heard_from: source === "tiktok-live" ? "TikTok Live" : "",
    tiktok_username: "", notes: "",
    consent_review: false, consent_decline: false, consent_travel: false, consent_updates: false,
  });

  useEffect(() => {
    if (open && pkg) {
      setStep(1); setReceipt(null); setReservationDone(false);
      setForm((f) => ({
        ...f,
        destination: pkg.city && pkg.city !== "Global" && pkg.city !== "Tour Cities" ? pkg.city : "",
        bid_amount: pkg.price_label ? "" : "",
        heard_from: source === "tiktok-live" ? "TikTok Live" : f.heard_from,
      }));
    }
  }, [open, pkg, source]);

  if (!open || !pkg) return null;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const chk = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  const allConsented = CONSENTS.every((c) => form[c.key]);

  const submit = async (e) => {
    e?.preventDefault(); setSubmitting(true);
    try {
      const payload = { ...form, package_slug: pkg.slug, source };
      const res = await applyAuction(payload);
      setReceipt({
        application_number: res.application_number,
        next_step: res.next_step,
        reservation_recommended: res.reservation_recommended,
        assignee: res.assignee,
      });
      toast.success(`Application received · #${res.application_number}`);
    } catch (err) {
      if (err?.response?.status === 409) toast.error("This package is fully allocated.");
      else if (err?.response?.status === 400) toast.error("Please confirm all consent checkboxes.");
      else toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  const payReservation = async () => {
    // MOCKED — real integration should route to Stripe Checkout for reservation_recommended amount.
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));
    try {
      // Optimistically mark reservation-paid via backend (real flow uses payment webhook).
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auction/reserve/${receipt.application_number}`;
      await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amount: receipt.reservation_recommended || 3000 }) });
      setReservationDone(true);
      toast.success("Reservation confirmed · position locked");
    } catch { toast.error("Reservation could not be completed."); }
    finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[95] bg-black/75 backdrop-blur-md overflow-y-auto"
        onClick={onClose}
        data-testid="app-form-modal"
      >
        <div className="min-h-full flex items-center justify-center p-3 py-12">
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl"
          >
            <div className="h-2 bg-gradient-to-r from-[#c8102e] via-[#c9a24b] to-[#4b2e83]" />

            <div className="p-5 md:p-8">
              <button type="button" onClick={onClose} className="float-right text-black/50 hover:text-black" data-testid="app-form-close">✕</button>
              <div className="text-[10px] tracking-[0.28em] uppercase text-black/60">Application · {pkg.city}</div>
              <h3 className="mt-2 font-display text-2xl md:text-3xl text-black leading-tight">{pkg.title}</h3>
              <div className="text-black/60 text-sm mt-1">{pkg.window_label} · {pkg.price_label || `$${pkg.opening_bid.toLocaleString()}`}</div>

              {receipt ? (
                // ── Confirmation screen ──
                <div className="mt-6" data-testid="app-form-receipt">
                  <div className="rounded-2xl border border-[#0f5d3a]/30 bg-[#0f5d3a]/5 p-5">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#0f5d3a]">Application received</div>
                    <div className="mt-2 stat-num text-3xl md:text-4xl text-[#0a0908]" data-testid="app-form-number">#{receipt.application_number}</div>
                    <p className="mt-3 text-sm text-black/75">{receipt.next_step}</p>
                    {receipt.assignee && (
                      <div className="mt-3 text-[10px] tracking-[0.24em] uppercase text-black/55">Assigned to <span className="text-[#0a0908] font-semibold">{receipt.assignee}</span></div>
                    )}
                  </div>

                  {receipt.reservation_recommended ? (
                    <div className="mt-5 rounded-2xl border border-[#c9a24b]/40 bg-[#fff6dc] p-5" data-testid="app-form-reservation">
                      <div className="text-[10px] tracking-[0.28em] uppercase text-[#7a5f22]">Recommended reservation</div>
                      <div className="mt-1 flex items-baseline gap-3">
                        <div className="stat-num text-3xl text-[#0a0908]">${receipt.reservation_recommended.toLocaleString()}</div>
                        <div className="text-xs text-black/60">applied toward the final winning price</div>
                      </div>
                      <p className="mt-3 text-sm text-black/75">Approved applicants can secure the position now. Public inventory is only reduced after your reservation payment clears.</p>
                      {reservationDone ? (
                        <div className="mt-4 rounded-full bg-[#0f5d3a] text-white px-5 py-3 text-[12px] tracking-[0.22em] uppercase font-bold text-center" data-testid="app-form-reservation-done">Position Locked ✓</div>
                      ) : (
                        <button onClick={payReservation} disabled={submitting} className="mt-4 w-full rounded-full py-3.5 font-bold tracking-[0.22em] uppercase text-[12px]" style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)", color: "#0a0908" }} data-testid="app-form-reservation-pay">
                          {submitting ? "Processing…" : "Pay Secure Reservation"}
                        </button>
                      )}
                      <div className="text-[10px] tracking-[0.2em] uppercase text-black/50 mt-3 text-center">Secure payment · No card data stored on this website</div>
                    </div>
                  ) : (
                    <div className="mt-5 text-xs text-black/60">You&apos;ll receive a secure reservation link if approved.</div>
                  )}

                  <button onClick={onClose} className="mt-6 w-full rounded-full py-3 text-[12px] tracking-[0.22em] uppercase font-semibold bg-black text-white" data-testid="app-form-done">Close</button>
                </div>
              ) : (
                // ── Multi-step form ──
                <form onSubmit={submit} className="mt-5" data-testid="app-form">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-black/50 mb-4">
                    {[1,2,3].map((n) => (
                      <span key={n} className={`px-2.5 py-1 rounded-full ${step >= n ? "bg-black text-white" : "bg-black/10 text-black/60"}`}>Step {n}</span>
                    ))}
                  </div>

                  {step === 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block md:col-span-2"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Full name</span>
                        <input required value={form.name} onChange={upd("name")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-name"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Email</span>
                        <input required type="email" value={form.email} onChange={upd("email")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-email"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Mobile number</span>
                        <input required value={form.phone} onChange={upd("phone")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-phone"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">WhatsApp number</span>
                        <input value={form.whatsapp} onChange={upd("whatsapp")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-whatsapp"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">City</span>
                        <input value={form.city} onChange={upd("city")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-city"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Country</span>
                        <input value={form.country} onChange={upd("country")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-country"/>
                      </label>
                      <div className="md:col-span-2 flex justify-end pt-2">
                        <button type="button" onClick={() => setStep(2)} className="rounded-full px-6 py-3 font-bold tracking-[0.22em] uppercase text-[12px] bg-black text-white disabled:opacity-40" disabled={!form.name || !form.email || !form.phone} data-testid="apx-next-1">Next →</button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Dog&apos;s name</span>
                        <input value={form.dog_name} onChange={upd("dog_name")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-dog"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Breed</span>
                        <input value={form.breed} onChange={upd("breed")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-breed"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Age</span>
                        <input value={form.dog_age} onChange={upd("dog_age")} placeholder="e.g. 2 years" className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-age"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Sex</span>
                        <select value={form.dog_sex} onChange={upd("dog_sex")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-sex">
                          <option value="">Select</option>
                          {SEX_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </label>
                      <label className="block md:col-span-2"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Weight</span>
                        <input value={form.dog_weight} onChange={upd("dog_weight")} placeholder="e.g. 65 lb" className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-weight"/>
                      </label>
                      <label className="block md:col-span-2"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Training challenges</span>
                        <textarea value={form.challenges} onChange={upd("challenges")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black min-h-[60px]" data-testid="apx-challenges"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Bite / aggression history</span>
                        <input value={form.bite_history} onChange={upd("bite_history")} placeholder="None / details" className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-bite"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Medical conditions</span>
                        <input value={form.medical} onChange={upd("medical")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-medical"/>
                      </label>
                      <div className="md:col-span-2 flex justify-between pt-2">
                        <button type="button" onClick={() => setStep(1)} className="rounded-full px-6 py-3 font-bold tracking-[0.22em] uppercase text-[12px] border border-black/20 text-black" data-testid="apx-back-2">← Back</button>
                        <button type="button" onClick={() => setStep(3)} className="rounded-full px-6 py-3 font-bold tracking-[0.22em] uppercase text-[12px] bg-black text-white" data-testid="apx-next-2">Next →</button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Requested destination</span>
                        <input value={form.destination} onChange={upd("destination")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-destination"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Bid amount</span>
                        <input value={form.bid_amount} onChange={upd("bid_amount")} placeholder={pkg.price_label || `$${pkg.opening_bid.toLocaleString()}`} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-bid"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Preferred training dates</span>
                        <input value={form.preferred_dates} onChange={upd("preferred_dates")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-dates"/>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Merchandise size</span>
                        <select value={form.merch_size} onChange={upd("merch_size")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-merch">
                          <option value="">Select</option>
                          {MERCH_SIZES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">How did you hear about Toriano?</span>
                        <select value={form.heard_from} onChange={upd("heard_from")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-heard">
                          <option value="">Select</option>
                          {HEARD_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </label>
                      <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">TikTok username</span>
                        <input value={form.tiktok_username} onChange={upd("tiktok_username")} placeholder="@yourhandle" className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="apx-tiktok"/>
                      </label>
                      <label className="block md:col-span-2"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Additional notes</span>
                        <textarea value={form.notes} onChange={upd("notes")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black min-h-[60px]" data-testid="apx-notes"/>
                      </label>

                      {/* Consent */}
                      <div className="md:col-span-2 mt-4 rounded-2xl border border-black/10 bg-black/5 p-4 space-y-3">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-black/60 mb-2">Required confirmations</div>
                        {CONSENTS.map((c) => (
                          <label key={c.key} className="flex items-start gap-3 cursor-pointer" data-testid={`apx-${c.key}`}>
                            <input type="checkbox" checked={form[c.key]} onChange={chk(c.key)} className="mt-1 w-4 h-4 accent-black flex-shrink-0"/>
                            <span className="text-[13px] text-black/85 leading-snug">{c.label}</span>
                          </label>
                        ))}
                      </div>

                      <div className="md:col-span-2 flex justify-between pt-2">
                        <button type="button" onClick={() => setStep(2)} className="rounded-full px-6 py-3 font-bold tracking-[0.22em] uppercase text-[12px] border border-black/20 text-black" data-testid="apx-back-3">← Back</button>
                        <button type="submit" disabled={submitting || !allConsented} className="rounded-full px-6 py-3 font-bold tracking-[0.22em] uppercase text-[12px] disabled:opacity-40" style={{ background: "linear-gradient(90deg,#c8102e,#7a1220)", color: "#ffffff" }} data-testid="apx-submit">
                          {submitting ? "Submitting…" : "Submit Application"}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
